import React, { useEffect, useRef, useState } from 'react';
import styles from './tooltip.css';
import PopoutContainer from '../popout/popout-container';
import { baseStyles, GeometryCss, getGeometry, PopoutProps } from '../popout/popout-helpers';
import classNames from 'classnames';

interface TooltipProps extends PopoutProps {
  /** Force tooltip to always be shown */
  forceVisible?: boolean;
  /** Provides a data-testid */
  'data-testid'?: string;
}

/**
 * Tooltip Component
 *
 * @param {TooltipProps} param
 *
 * @returns JSX.ELEMENT
 */
export const Tooltip = ({
  trigger,
  children,
  xAlign,
  yAlign,
  containerProps,
  className,
  activeClass,
  light,
  disabled,
  forceVisible,
  width: containerWidth = 200,
  'data-testid': dataTestid
}: TooltipProps) => {
  const [visible, setVisible] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const wrapperRef = useRef<HTMLSpanElement>(null);
  const { current } = wrapperRef;
  const [geometry, setGeometry] = useState<GeometryCss>(getGeometry(current, { xAlign, yAlign }));

  useEffect(() => {
    setGeometry(getGeometry(current, { xAlign, yAlign }));
  }, [wrapperRef.current]);

  const show = () => {
    if (!disabled) {
      setVisible(true);
    }
  };

  const hide = () => {
    setVisible(false);
  };

  useEffect(() => {
    if (forceVisible) {
      show();
    }
  }, []);

  return (
    <span
      ref={wrapperRef}
      className={classNames([visible && activeClass, className, styles.wrapper])}
      onMouseEnter={show}
      onMouseLeave={hide}
      data-testid={dataTestid}
    >
      {trigger}
      {wrapperRef && (
        <PopoutContainer
          {...{
            ...containerProps,
            containerRef,
            containerWidth,
            geometry,
            light,
            style: baseStyles(visible || forceVisible, containerWidth, geometry),
            type: 'tooltip'
          }}
        >
          {children}
        </PopoutContainer>
      )}
    </span>
  );
};

export default Tooltip;
